import React, { createContext, useContext, useState } from 'react';
import { useNavigationPreferences, NavigationItem } from '@/hooks/useNavigationPreferences';

interface NavigationPreferencesContextType {
  preferences: NavigationItem[] | null;
  isLoading: boolean;
  isCustomizerOpen: boolean;
  openCustomizer: () => void;
  closeCustomizer: () => void;
  isItemVisible: (path: string) => boolean;
  getItemOrder: (path: string) => number;
}

const NavigationPreferencesContext = createContext<NavigationPreferencesContextType | undefined>(undefined);

export const useNavigationPreferencesContext = () => {
  const context = useContext(NavigationPreferencesContext);
  if (!context) {
    throw new Error('useNavigationPreferencesContext must be used within NavigationPreferencesProvider');
  }
  return context;
};

interface NavigationPreferencesProviderProps {
  children: React.ReactNode;
}

export const NavigationPreferencesProvider: React.FC<NavigationPreferencesProviderProps> = ({
  children,
}) => {
  const { preferences, isLoading } = useNavigationPreferences();
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);

  const isItemVisible = (path: string): boolean => {
    if (!preferences?.items) return true; // Default to visible
    const item = preferences.items.find(item => item.path === path);
    return item?.visible ?? true;
  };

  const getItemOrder = (path: string): number => {
    if (!preferences?.items) return 0;
    const item = preferences.items.find(item => item.path === path);
    return item?.order ?? 0;
  };

  return (
    <NavigationPreferencesContext.Provider
      value={{
        preferences: preferences?.items || null,
        isLoading,
        isCustomizerOpen,
        openCustomizer: () => setIsCustomizerOpen(true),
        closeCustomizer: () => setIsCustomizerOpen(false),
        isItemVisible,
        getItemOrder,
      }}
    >
      {children}
    </NavigationPreferencesContext.Provider>
  );
};